import React, { Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import Loader from "../Layout/Loader";
import { authRoutes } from "./AuthRoutes";
import LayoutRoutes from "./LayoutRoutes";
import PrivateRoute from "./PrivateRoute";
import { useAuth } from "../_helper/Auth/AuthProvider";
import { getRedirectPath, needsEducationalLevel, needsSubscription } from "./redirectUtils";

// Lazy load components
const Login = React.lazy(() => import("../Components/Pages/Auth/Login"));
const RegisterStepThree = React.lazy(() => import("../Components/Pages/Auth/RegisterWizard/RegisterStepThree"));
const RegisterStepFour = React.lazy(() => import("../Components/Pages/Auth/RegisterWizard/RegisterStepFour"));
const PaymentForm = React.lazy(() => import("../Auth/PaymentForm"));
const PrivacyPolicy = React.lazy(() => import("../Components/Pages/Privacy/PrivacyPolicy"));

const Routers = () => {
  const { authenticated, userData, initialized } = useAuth();

  // console.log("Routers - Current auth state:", { authenticated, userData, loading, initialized });

  if (!initialized) {
    return <Loader />;
  }

  const redirectPath = getRedirectPath(authenticated, userData);
  const isFullyAuthenticated = authenticated && userData && userData.role;

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/" element={<Navigate to={redirectPath} />} />
        <Route path="/auth/login" element={isFullyAuthenticated ? <Navigate to={redirectPath} /> : <Login />} />

        {/* Public Privacy Policy Route - No authentication required */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Special case for educational level selection */}
        <Route path="/auth/register/educational-level" element={isFullyAuthenticated && !needsEducationalLevel(userData) ?
          <Navigate to={redirectPath} /> : <RegisterStepThree />} />

        {/* Special case for billing/subscription page */}
        <Route path="/auth/register/billing" element={isFullyAuthenticated && !needsSubscription(userData) ?
          <Navigate to={redirectPath} /> : <RegisterStepFour />} />

        {/* Special case for checkout - needs to work for authenticated users without subscription */}
        <Route path="/auth/checkout" element={
          !authenticated ? <Navigate to="/auth/login" /> : <PaymentForm />
        } />

        <Route element={<PrivateRoute roles={["admin", "user"]} />}>
          <Route path="/*" element={<LayoutRoutes />} />
        </Route>

        {authRoutes
          .filter(route =>
            route.path !== "/auth/register/educational-level" &&
            route.path !== "/auth/register/billing" &&
            route.path !== "/auth/checkout" &&
            route.path !== "/privacy-policy"
          )
          .map(({ path, Component }, i) => (
            <Route
              path={path}
              element={isFullyAuthenticated ? <Navigate to={redirectPath} /> : <Component />}
              key={i}
            />
          ))}
      </Routes>
    </Suspense>
  );
};

export default Routers;